import { Group, Radio } from "@mantine/core";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ModalConfirmCustom from "../../../../components/custom/modal-confirm-custom";
import { TabCustom, TabsCustom } from "../../../../components/custom/tab-custom";
import {
  ATTENDANCE_ISSUE_TYPE,
  PAGE_SIZES,
} from "../../../../constants/status";
import { STRING_MENU, STRING_SUB_MENU } from "../../../../constants/string";
import useDebounce from "../../../../hooks/useDebounce";
import { usePageTitle } from "../../../../hooks/usePageTitle";
import {
  useCRUDResponse,
  useTablePaginationResponse,
} from "../../../../hooks/useResponseHandler";
import { AttendanceGridRow, AttendanceRecord } from "../types/attendance";
import {
  setSidebarMenu,
  setSidebarMenuSub,
  toggleMenuContent,
} from "../../../../store/themeConfigSlice";
import { getCurrentMonth, getFirstDayOfMonth, getTodayDate } from "../../../../utils/time-utils";
import AttendanceManagementFilter from "../components/attendance-management-filter";
import AttendanceManagementTable from "../components/attendance-management-table";
import AttendanceManagementGrid from "../components/attendance-management-grid";
import AttendanceExportExcelModal from "../modals/attendance-export-excel-modal";
import AttendanceManagementDetailModal from "../modals/attendance-management-detail-modal";
import AttendanceManagementMonthlyModal from "../modals/attendance-management-monthly-modal";
import { AttendanceService } from "../services/attendance-service";
import { ButtonCustom } from "../../../../components/custom/button-custom";
import IconExcelFile from "../../../../components/icons/icon-excel-file";
import { exportMonthlyAttendanceExcel } from "../utils/attendance-excel-exporter";
import { useStoreOptions } from "../../../../hooks/use-store-options";
import { showToast } from "../../../../components/custom/custom-toast";

const TAB_DAILY = 0;
const TAB_MONTHLY = 1;

const AttendanceManagementPage = () => {
  const dispatch = useDispatch();
  usePageTitle(STRING_SUB_MENU.ATTENDANCE_MANAGEMENT);
  useEffect(() => {
    dispatch(toggleMenuContent(STRING_SUB_MENU.ATTENDANCE_MANAGEMENT));
    dispatch(setSidebarMenu(STRING_MENU.HUMAN_RESOURCE_MANAGEMENT));
    dispatch(setSidebarMenuSub(STRING_SUB_MENU.PAYROLL_MANAGEMENT));
  }, []);

  const [activeTab, setActiveTab] = useState(TAB_DAILY);

  const [dataStringInput, setDateStringInput] = useState(getTodayDate());
  const [dataString, setDateString] = useState(dataStringInput);
  const [keySearch, setKeySearch] = useState("");
  // Dùng chung cho cả 2 tab — đổi tab không mất lựa chọn chi nhánh.
  const [branchId, setBranchId] = useState<number | "">("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZES[0]);
  const [dataTable, setDataTable] = useState<AttendanceRecord[]>([]);

  // Tab "Theo tháng" (Lưới chấm công) — mặc định từ ngày đầu tháng đến ngày hiện tại
  const [fromDateInput, setFromDateInput] = useState(getFirstDayOfMonth());
  const [toDateInput, setToDateInput] = useState(getTodayDate());
  const [fromDate, setFromDate] = useState(fromDateInput);
  const [toDate, setToDate] = useState(toDateInput);
  const [attendanceModes, setAttendanceModes] = useState<number[]>([]);
  const [gridPage, setGridPage] = useState(1);
  const [gridLimit, setGridLimit] = useState(PAGE_SIZES[0]);
  const [gridDataTable, setGridDataTable] = useState<AttendanceGridRow[]>([]);
  const [gridTotalRecords, setGridTotalRecords] = useState(0);
  const [selectedItem, setSelectedItem] = useState<AttendanceRecord>(
    new AttendanceRecord()
  );
  const [selectedRecord, setSelectedRecord] = useState<AttendanceRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [stateConfirm, setStateConfirm] = useState({
    employeeId: 0,
    workDate: "",
    issueType: 0,
  });
  const [noteConfirm, setNoteConfirm] = useState("");
  const [coefficientWorkDay, setCoefficientWorkDay] = useState(1);
  const [isShowModalMonthly, setIsShowModalMonthly] = useState(false);
  const [isShowExportModal, setIsShowExportModal] = useState(false);
  const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
  const [refreshTableRecord, setRefreshTableRecord] = useState(false);
  const [isShowModalDetail, setIsShowModalDetail] = useState(false);

  const {
    fetch: onGetAttendance,
    response: resAttendance,
    loading: loadingTable,
  } = AttendanceService.getAttendanceDaily();
  const {
    fetch: onGetAttendanceGrid,
    response: resAttendanceGrid,
    loading: loadingGrid,
  } = AttendanceService.getAttendanceGrid();
  const {
    fetch: onApproveIssue,
    response: resApproveIssue,
    loading: loadingApprove,
  } = AttendanceService.approveIssue();

  const { options: branchOptions } = useStoreOptions();
  const {
    fetch: onExportGrid,
    loading: loadingExportGrid,
  } = AttendanceService.getAttendanceGridExport();

  const handleExportMonthlyExcel = async () => {
    const selectedBranch = branchOptions.find((b) => b.value === branchId);
    const branchName = selectedBranch ? selectedBranch.label : "Tất cả chi nhánh";
    try {
      const res = await onExportGrid({
        from_date: fromDate,
        to_date: toDate,
        key_search: keySearch,
        ...(branchId ? { branch_id: branchId } : {}),
        ...(attendanceModes.length > 0 ? { attendance_modes: attendanceModes.join(",") } : {}),
      });
      if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
        await exportMonthlyAttendanceExcel(res.data, fromDate, toDate, branchName);
      } else {
        showToast({
          message: res?.message || "Không có nhân viên nào có công trong khoảng thời gian này!",
          type: "warning",
        });
      }
    } catch (e: any) {
      showToast({
        message: e?.message || "Lỗi khi lấy dữ liệu xuất Excel!",
        type: "error",
      });
    }
  };

  // Chỉ gọi API của tab đang mở — đổi tab không tốn API thừa cho tab còn lại.
  useEffect(() => {
    if (activeTab !== TAB_DAILY) return;
    onGetAttendance({
      page: page,
      limit: limit,
      date: dataString,
      key_search: keySearch,
      ...(branchId ? { branch_id: branchId } : {}),
    });
  }, [activeTab, page, limit, dataString, keySearch, branchId]);

  useEffect(() => {
    if (activeTab !== TAB_MONTHLY) return;
    onGetAttendanceGrid({
      page: gridPage,
      limit: gridLimit,
      from_date: fromDate,
      to_date: toDate,
      key_search: keySearch,
      ...(branchId ? { branch_id: branchId } : {}),
      ...(attendanceModes.length > 0 ? { attendance_modes: attendanceModes.join(",") } : {}),
    });
  }, [activeTab, gridPage, gridLimit, fromDate, toDate, keySearch, branchId, attendanceModes]);

  useDebounce(
    () => {
      setPage(1);
      setDateString(dataStringInput);
    },
    500,
    [dataStringInput]
  );

  useDebounce(
    () => {
      setGridPage(1);
      setFromDate(fromDateInput);
      setToDate(toDateInput);
    },
    500,
    [fromDateInput, toDateInput]
  );

  useTablePaginationResponse(resAttendance, setDataTable, setTotalRecords);
  useTablePaginationResponse(resAttendanceGrid, setGridDataTable, setGridTotalRecords);
  useCRUDResponse(
    resApproveIssue,
    () => {
      setIsShowModalConfirm(false);
      setRefreshTableRecord(true);
      setStateConfirm({ employeeId: 0, workDate: "", issueType: 0 });
      setNoteConfirm("");
    },
    `Duyệt phiều ${stateConfirm.issueType === ATTENDANCE_ISSUE_TYPE.LATE_CHECK_IN
      ? "check in muộn"
      : stateConfirm.issueType === ATTENDANCE_ISSUE_TYPE.EARLY_CHECK_OUT
        ? "check out sớm"
        : "chấm công nhiều lần"
    } thành công`
  );

  return (
    <>
      <div className="flex flex-col gap-3">
        {/* <div className="flex flex-wrap items-center justify-end gap-3" id="attendance-management-header">
          <ButtonCustom
            text="Xuất excel"
            onClick={() => setIsShowExportModal(true)}
            icon={<IconExcelFile />}
            className="font-bold text-green-600 uppercase bg-green-200 hover:bg-green-600 hover:text-white btn"
          />
        </div> */}

        <TabsCustom
          value={activeTab}
          onChange={setActiveTab}
          className="border-b"
          id="attendance-management-tabs"
          lineColor="#16a34a"
        >
          <TabCustom label="Theo ngày" colorSelected="#15803d" colorUnSelected="#64748b" />
          <TabCustom label="Theo tháng" colorSelected="#15803d" colorUnSelected="#64748b" />
        </TabsCustom>

        {activeTab === TAB_DAILY ? (
          <div className="flex flex-col gap-3 panel">
            <AttendanceManagementFilter
              dateTime={dataString}
              setDateTime={setDateStringInput}
              keySearch={keySearch}
              setKeySearch={(value) => {
                setPage(1);
                setKeySearch(value);
              }}
              branchId={branchId}
              setBranchId={(value) => {
                setPage(1);
                setBranchId(value);
              }}
            />

            <AttendanceManagementTable
              dataTable={dataTable}
              loadingTable={loadingTable}
              page={page}
              limit={limit}
              totalRecords={totalRecords}
              setPage={setPage}
              setLimit={setLimit}
              onShowModal={(selectedItem) => {
                setSelectedItem(selectedItem);
                setIsShowModalMonthly(true);
              }}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3 panel">
            <AttendanceManagementFilter
              isDateRange={true}
              fromDate={fromDateInput}
              setFromDate={setFromDateInput}
              toDate={toDateInput}
              setToDate={setToDateInput}
              attendanceModes={attendanceModes}
              setAttendanceModes={(modes) => {
                setGridPage(1);
                setAttendanceModes(modes);
              }}
              keySearch={keySearch}
              setKeySearch={(value) => {
                setGridPage(1);
                setKeySearch(value);
              }}
              branchId={branchId}
              setBranchId={(value) => {
                setGridPage(1);
                setBranchId(value);
              }}
              onExportExcel={handleExportMonthlyExcel}
              loadingExport={loadingExportGrid}
            />

            <AttendanceManagementGrid
              dataTable={gridDataTable}
              loadingTable={loadingGrid}
              fromDate={fromDate}
              toDate={toDate}
              page={gridPage}
              limit={gridLimit}
              totalRecords={gridTotalRecords}
              setPage={setGridPage}
              setLimit={setGridLimit}
              onShowModal={(selectedItem) => {
                setSelectedItem(selectedItem);
                setIsShowModalMonthly(true);
              }}
            />
          </div>
        )}
      </div>

      <AttendanceManagementMonthlyModal
        isShow={isShowModalMonthly}
        onClose={() => setIsShowModalMonthly(false)}
        selectedItem={selectedItem}
        refreshTable={refreshTableRecord}
        setRefreshTable={setRefreshTableRecord}
        onShowModalConfirm={({ employeeId, workDate, issueType }) => {
          setStateConfirm({ employeeId, workDate, issueType });
          setIsShowModalConfirm(true);
        }}
        onShowDetail={(itemSelected) => {
          setSelectedRecord(itemSelected);
          setIsShowModalDetail(true);
        }}
      />

      {/* <AttendanceExportExcelModal
        isShow={isShowExportModal}
        onClose={() => setIsShowExportModal(false)}
      /> */}

      <ModalConfirmCustom
        isLoading={loadingApprove}
        isOpen={isShowModalConfirm}
        titleModal={`Xác nhận duyệt phiếu ${stateConfirm.issueType === ATTENDANCE_ISSUE_TYPE.LATE_CHECK_IN
          ? "check in muộn"
          : stateConfirm.issueType === ATTENDANCE_ISSUE_TYPE.EARLY_CHECK_OUT
            ? "check out sớm"
            : "chấm công nhiều lần"
          }`}
        typeIcon={"none"}
        content={
          <div className="flex flex-col w-full gap-3">
            <div className="flex flex-col items-center w-full gap-2 panel">
              <span className="text-base font-medium text-slate-800">
                Bạn có chắc chắn muốn duyệt phiếu{" "}
                {stateConfirm.issueType === ATTENDANCE_ISSUE_TYPE.LATE_CHECK_IN
                  ? "check in muộn"
                  : stateConfirm.issueType ===
                    ATTENDANCE_ISSUE_TYPE.EARLY_CHECK_OUT
                    ? "check out sớm"
                    : "chấm công nhiều lần"}
                {" này?"}
              </span>
              <span className="text-base font-medium text-slate-800">
                Vui lòng chọn hình thức duyệt:
              </span>
              <div className="">
                <Radio.Group
                  withAsterisk
                  name="salaryOption"
                  value={coefficientWorkDay.toString()}
                  onChange={(value) => setCoefficientWorkDay(Number(value))}
                >
                  <Group mt="xs">
                    <Radio value={"1"} label="1 ngày công" />
                    <Radio value={"0.5"} label="0.5 ngày công" />
                  </Group>
                </Radio.Group>
              </div>
              <textarea
                required
                value={noteConfirm}
                rows={5}
                className="w-full resize-none form-textarea"
                placeholder="Vui lòng nhập ghi chú"
                onChange={(e) => {
                  setNoteConfirm(e.target.value);
                }}
              />
            </div>
          </div>
        }
        onClose={() => setIsShowModalConfirm(false)}
        onConfirm={() => {
          onApproveIssue({
            employee_id: stateConfirm.employeeId,
            work_date: stateConfirm.workDate,
            issue_type: stateConfirm.issueType,
            work_day_coefficient: coefficientWorkDay,
            notes: noteConfirm,
          });
        }}
      />

      <AttendanceManagementDetailModal
        isShow={isShowModalDetail}
        onClose={() => setIsShowModalDetail(false)}
        selectedRecord={selectedRecord}
      />
    </>
  );
};

export default AttendanceManagementPage;
